import { Button, HStack, Icon, Separator, Stack, Text } from '@chakra-ui/react'
import type { ScoProduct } from '@src/graphql-types'
import { useId, useState } from 'react'
import { LuCheck } from 'react-icons/lu'

import { cardFeatureGroups, type CardFeatureGroup } from './cardLists'
import {
  resolvePaywallIntent,
  visiblePaywallFeatures,
  type PaywallFeature,
  type PaywallIntentId,
} from './intent'
import { productLabel } from './presentation'

const FEATURE_AREAS = [
  {
    id: 'advice',
    slugs: [
      'team-dm',
      'wil-chat',
      'marketplace-message',
      'board-invite',
      'fast-send',
      'expert-sessions',
      'advisor-sprints',
      'browse-advisors',
      'board-seat',
      'board-applications',
      'paid-sessions',
    ],
  },
  {
    id: 'funding',
    slugs: [
      'messages-per-day',
      'investor-directory',
      'unlimited-matches',
      'pitch-tools',
      'unlimited-outreach',
    ],
  },
  {
    id: 'learning',
    slugs: ['courses', 'goals', 'live-workshops'],
  },
  {
    id: 'community',
    slugs: ['member-matching', 'global-founders', 'perks'],
  },
] as const

/**
 * A sitewide plan reads as breadth: the lead line of each area first (advice, funding, learning,
 * community), then each area's next line, so any cut of the list still spans the whole plan.
 */
function interleaveByArea(features: PaywallFeature[]) {
  const assigned = new Set<PaywallFeature>()
  const areas = FEATURE_AREAS.map(area => {
    const included = features
      .filter(feature => area.slugs.some(slug => slug === feature.key.toLowerCase()))
      .sort(
        (left, right) =>
          area.slugs.findIndex(slug => slug === left.key.toLowerCase()) -
          area.slugs.findIndex(slug => slug === right.key.toLowerCase())
      )
    included.forEach(feature => assigned.add(feature))
    return included
  })
  const longest = Math.max(0, ...areas.map(area => area.length))
  const ordered: PaywallFeature[] = []
  for (let index = 0; index < longest; index++) {
    areas.forEach(area => {
      if (area[index]) {
        ordered.push(area[index])
      }
    })
  }
  return [...ordered, ...features.filter(feature => !assigned.has(feature))]
}

function FeatureLine({ feature }: { feature: PaywallFeature }) {
  const Bullet = feature.icon ?? LuCheck
  return (
    <HStack align={'flex-start'} as={'li'} gap={2.5}>
      <Icon aria-hidden boxSize={4} color={'orange.fg'} flexShrink={0} mt={'0.2em'} strokeWidth={2.5}>
        <Bullet />
      </Icon>
      <Stack gap={0.5}>
        <Text textStyle={'sm'}>{feature.title}</Text>
        {feature.description ? (
          <Text color={'fg.muted'} textStyle={'xs'}>
            {feature.description}
          </Text>
        ) : null}
      </Stack>
    </HStack>
  )
}

/** A capped list whose remainder opens in place: the count sells, the lines stay one tap away. */
function ExpandableFeatureList({
  features,
  label,
  limit,
  name,
}: {
  features: PaywallFeature[]
  label: string
  limit?: number
  name: string
}) {
  const listId = useId()
  const [expanded, setExpanded] = useState(false)
  // Hiding a single line costs a tap to save one row; show it instead.
  const capped = Boolean(limit && limit > 0 && features.length > limit + 1)
  const shown = capped && !expanded ? features.slice(0, limit) : features
  const hidden = features.length - (limit ?? features.length)

  return (
    <Stack gap={3}>
      <Stack aria-label={label} as={'ul'} gap={3} id={listId} listStyle={'none'}>
        {shown.map(feature => (
          <FeatureLine key={feature.key} feature={feature} />
        ))}
      </Stack>
      {capped ? (
        <Button
          _hover={{ color: 'fg', textDecorationColor: 'currentColor' }}
          alignSelf={'flex-start'}
          aria-controls={listId}
          aria-expanded={expanded}
          color={'fg.muted'}
          h={'auto'}
          minW={0}
          ms={'1.625rem'}
          px={0}
          py={0.5}
          size={'sm'}
          textDecoration={'underline'}
          textDecorationColor={'border.emphasized'}
          textStyle={'xs'}
          textUnderlineOffset={'3px'}
          variant={'plain'}
          onClick={() => setExpanded(open => !open)}
        >
          {expanded ? 'Show fewer' : `+ ${hidden} more with ${name}`}
        </Button>
      ) : null}
    </Stack>
  )
}

export type PaywallFeatureListProps = {
  product: ScoProduct
  intent?: PaywallIntentId
  /** A cheaper plan this one contains. The list leads with "Everything in …, plus" and only the additions. */
  includedPlan?: ScoProduct | null
  /** Lines shown before "+ N more", which expands the rest in place. Omit to list everything. */
  limit?: number
}

function ComposedFeatureList({
  groups,
  limit,
  name,
}: {
  groups: CardFeatureGroup[]
  limit?: number
  name: string
}) {
  const listId = useId()
  const [expanded, setExpanded] = useState(false)
  const total = groups.reduce((count, group) => count + group.features.length, 0)
  const capped = Boolean(limit && limit > 0 && total > limit + 1)
  const shownCount = capped && !expanded ? (limit ?? total) : total
  const visible = groups.reduce<CardFeatureGroup[]>((shown, group) => {
    const used = shown.reduce((count, item) => count + item.features.length, 0)
    const room = shownCount - used
    if (room <= 0) {
      return shown
    }
    const features = group.features.slice(0, room)
    return features.length > 0 ? [...shown, { ...group, features }] : shown
  }, [])
  const hidden = total - (limit ?? total)

  return (
    <Stack gap={3}>
      <Stack gap={3} id={listId}>
        {visible.map(group => (
          <Stack key={group.separator ?? group.label ?? 'lead'} gap={3}>
            {group.label ? (
              <Text fontWeight={'semibold'} textStyle={'sm'}>
                {group.label}
              </Text>
            ) : null}
            {group.separator ? (
              <HStack gap={3}>
                <Text color={'fg.muted'} textStyle={'label'}>
                  {group.separator}
                </Text>
                <Separator flex={'1'} />
              </HStack>
            ) : null}
            <Stack aria-label={name} as={'ul'} gap={3} listStyle={'none'}>
              {group.features.map(feature => (
                <FeatureLine key={feature.key} feature={feature} />
              ))}
            </Stack>
          </Stack>
        ))}
      </Stack>
      {capped ? (
        <Button
          _hover={{ color: 'fg', textDecorationColor: 'currentColor' }}
          alignSelf={'flex-start'}
          aria-controls={listId}
          aria-expanded={expanded}
          color={'fg.muted'}
          h={'auto'}
          minW={0}
          ms={'1.625rem'}
          px={0}
          py={0.5}
          size={'sm'}
          textDecoration={'underline'}
          textDecorationColor={'border.emphasized'}
          textStyle={'xs'}
          textUnderlineOffset={'3px'}
          variant={'plain'}
          onClick={() => setExpanded(open => !open)}
        >
          {expanded ? 'Show fewer' : `+ ${hidden} more with ${name}`}
        </Button>
      ) : null}
    </Stack>
  )
}

export const PaywallFeatureList = ({ includedPlan, intent, limit, product }: PaywallFeatureListProps) => {
  const requestedId: PaywallIntentId = intent ?? 'default'
  const groups = cardFeatureGroups(product, requestedId)
  const name = productLabel(product)
  if (groups) {
    return <ComposedFeatureList groups={groups} limit={limit} name={name} />
  }

  const resolved = resolvePaywallIntent(intent)
  const features = visiblePaywallFeatures(product, resolved).filter(feature => feature.showOnCard !== false)

  if (features.length === 0) {
    return (
      <Text color={'fg.muted'} textStyle={'sm'}>
        Plan benefits aren’t available right now.
      </Text>
    )
  }

  if (includedPlan) {
    const inherited = new Map(
      visiblePaywallFeatures(includedPlan, resolved).map(feature => [feature.key, feature.title])
    )
    // A line the cheaper plan has with a smaller amount is an upgrade too, so it stays listed.
    const additions = features.filter(feature => inherited.get(feature.key) !== feature.title)
    if (additions.length > 0) {
      return (
        <Stack gap={3}>
          <Text fontWeight={'semibold'} textStyle={'sm'}>
            {`Everything in ${productLabel(includedPlan)}, plus:`}
          </Text>
          <ExpandableFeatureList features={additions} label={`${name} additions`} limit={limit} name={name} />
        </Stack>
      )
    }
  }

  return (
    <ExpandableFeatureList
      features={resolved.id === 'default' ? interleaveByArea(features) : features}
      label={`${name} features`}
      limit={limit}
      name={name}
    />
  )
}
